import React, { useRef, useState } from 'react'
import Header from '../../core/Header/Header'
import { Stack, TextField, Typography, Card, ToggleButton, ToggleButtonGroup, InputAdornment, IconButton } from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import './style.css'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { hideLoader, showLoader, setUserDetail, resetState } from '../../Store/mainSlice'
import httpClient from '../../_util/api'
import CustomizedSnackbars from '../../shared/Snackbar/SnackBar'
import logo from '../../assets/images/logo.png'
import ButtonMain from '../../components/Button'
import { getRedirectPath } from '../../Helpers/Redirect';
import { clearAuthSession, setAuthSessionExpiry } from '../../_util/session';

const Home = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const snackbar_Ref = useRef()
    const [loginType, setLoginType] = useState('email')
    const [showPassword, setShowPassword] = useState(false)
    const [userCredentials, setUserCredentials] = useState({ email: '', password: '', otp: '' })

    const handleLoginTypeChange = (event, newType) => {
        if (newType !== null) {
            setLoginType(newType)
            setUserCredentials((prev) => ({ ...prev, email: '' }))
        }
    }

    const handleInputChange = (data, field) => {
        if (field === "email") {
            if (loginType === 'cnic') {
                // CNIC formatting logic
                let digits = data.replace(/\D/g, '');
                
                if (digits.length >= 6) { 
                    digits = digits.slice(0, 13);
                    if (digits.length > 5 && digits.length <= 12) {
                        digits = digits.slice(0,5) + '-' + digits.slice(5);
                    }
                    if (digits.length > 12) {
                        digits = digits.slice(0,5) + '-' + digits.slice(5,12) + '-' + digits.slice(12);
                    }

                    setUserCredentials((prev) => ({ ...prev, [field]: digits }));
                } else {
                    setUserCredentials((prev) => ({ ...prev, [field]: data }));
                }
            } else {
                // Email - no special formatting
                setUserCredentials((prev) => ({ ...prev, [field]: data }));
            }
        } else {
            setUserCredentials((prevFormData) => ({ ...prevFormData, [field]: data }));
        }
    };

    
    const loginHandler = async () => {
        dispatch(showLoader())
        const res = await httpClient.post('/user/login', userCredentials).catch((error) => {
            dispatch(hideLoader())
            snackbar_Ref.current.showMessage("error", error?.response.data.message, "", "i-chk-circle");
        })
        if (res?.status === 200) {
            dispatch(hideLoader())
            const { token, role } = res.data;
            clearAuthSession();
            dispatch(resetState());
            localStorage.setItem('authToken', token);
            setAuthSessionExpiry();
            dispatch(setUserDetail(res.data));

            // If we got here because RoleProtectedRoute bounced an unauthenticated
            // visit off a deep link (e.g. a one-time application link), send them
            // back to it instead of the role's default landing page.
            const basePath = getRedirectPath(role);
            const from = location.state?.from;
            const fromPath = from ? `${from.pathname}${from.search || ''}` : null;
            navigate(fromPath && fromPath.startsWith(basePath) ? fromPath : basePath);
        }
    }

    return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100vh', backgroundColor: '#f5f5f5' }}>
            <Header />
            <CustomizedSnackbars ref={snackbar_Ref} />
            <Card sx={{ width: 350, padding: 4, textAlign: 'center', borderRadius: 3, boxShadow: 3 }}>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                    Login with
                </Typography>
                
                <ToggleButtonGroup
                    value={loginType}
                    exclusive
                    onChange={handleLoginTypeChange}
                    fullWidth
                    sx={{ 
                        mb: 2,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        '& .MuiToggleButtonGroup-grouped': {
                            border: '1px solid #e0e0e0',
                            borderRadius: '0',
                            margin: 0,
                            padding: '8px 16px',
                            '&:not(:first-of-type)': {
                                marginLeft: 0,
                                borderLeft: 'none',
                            },
                            '&.Mui-selected': {
                                backgroundColor: '#4caf50',
                                color: '#fff',
                                border: '1px solid #4caf50',
                                '&:hover': {
                                    backgroundColor: '#45a049',
                                }
                            },
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            }
                        }
                    }}
                >
                    <ToggleButton value="email" sx={{ textTransform: 'none', flex: 1, fontSize: '0.9rem' }}>
                        Email
                    </ToggleButton>
                    <ToggleButton value="cnic" sx={{ textTransform: 'none', flex: 1, fontSize: '0.9rem' }}>
                        CNIC
                    </ToggleButton>
                </ToggleButtonGroup>
                
                <TextField
                    label={loginType === 'email' ? 'Email' : 'CNIC'}
                    name="email"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    value={userCredentials.email} 
                    onChange={(e) => handleInputChange(e.target.value, "email")}
                    placeholder={loginType === 'email' ? 'Enter your email' : '00000-0000000-0'}
                />
                <TextField
                    label="Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    value={userCredentials.password}
                    onChange={(e) => handleInputChange(e.target.value, "password")}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <ButtonMain
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={loginHandler}
                >
                    Login
                </ButtonMain>
            </Card>
        </Stack>
    )
}

export default Home